import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Send, AlertTriangle, FileText, Sparkles, ThumbsUp, MessageSquare, Trash2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrandSelector = ({ selectedBrand, onBrandChange }) => (
  <div className="flex items-center gap-2" data-testid="brand-selector">
    <span className="text-sm font-medium text-gray-600">Sello:</span>
    <div className="flex gap-3">
      <button
        data-testid="visa-brand-button"
        onClick={() => onBrandChange('visa')}
        className={`px-3 py-2 rounded-lg transition-all flex items-center gap-2 ${
          selectedBrand === 'visa'
            ? 'bg-blue-50 border-2 border-blue-600 shadow-lg'
            : 'bg-white border-2 border-gray-200 hover:border-blue-300'
        }`}
      >
        <img 
          src="/visa-logo.png" 
          alt="Visa" 
          className="h-6 w-auto"
        />
      </button>
      <button
        data-testid="mastercard-brand-button"
        onClick={() => onBrandChange('mastercard')}
        className={`px-3 py-2 rounded-lg transition-all flex items-center gap-2 ${
          selectedBrand === 'mastercard'
            ? 'bg-red-50 border-2 border-red-600 shadow-lg'
            : 'bg-white border-2 border-gray-200 hover:border-red-300'
        }`}
      >
        <img 
          src="/mastercard-logo.png" 
          alt="Mastercard" 
          className="h-6 w-auto"
        />
      </button>
    </div>
  </div>
);

const FeedbackSection = ({ traceId, onFeedbackSubmit }) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) return;
    
    setIsSubmitting(true);
    try {
      await onFeedbackSubmit(traceId, rating, comment);
      setSubmitted(true);
      setTimeout(() => setShowFeedback(false), 2000);
    } catch (error) {
      console.error('Error al enviar feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
        <p className="text-sm text-green-700">¡Gracias por tu feedback!</p>
      </div>
    );
  }

  return (
    <div className="mt-3">
      {!showFeedback ? (
        <button
          onClick={() => setShowFeedback(true)}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
          data-testid="show-feedback-button"
        >
          <ThumbsUp className="w-4 h-4" />
          Calificar respuesta
        </button>
      ) : (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="feedback-form">
          <p className="text-sm font-medium text-gray-700 mb-2">¿Qué te pareció esta respuesta?</p>
          
          <div className="flex gap-2 mb-3">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                className={`w-8 h-8 rounded-full font-bold transition-all ${
                  rating >= star
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
                data-testid={`rating-${star}`}
              >
                {star}
              </button>
            ))}
          </div>

          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Comentarios adicionales (opcional)"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows="2"
            data-testid="feedback-comment"
          />

          <div className="flex gap-2 mt-2">
            <button
              onClick={handleSubmit}
              disabled={rating === 0 || isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              data-testid="submit-feedback-button"
            >
              {isSubmitting ? 'Enviando...' : 'Enviar'}
            </button>
            <button
              onClick={() => setShowFeedback(false)}
              className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Function to extract unique pages from all citations
const getUniquePages = (citations) => {
  if (!citations || citations.length === 0) return [];
  
  const allPages = new Set();
  
  citations.forEach(citation => {
    if (citation.references) {
      citation.references.forEach(ref => {
        if (ref.pages && Array.isArray(ref.pages)) {
          ref.pages.forEach(page => allPages.add(page));
        }
      });
    }
  });
  
  // Convert Set to Array and sort numerically
  return Array.from(allPages).sort((a, b) => a - b);
};

const Message = ({ message, isUser, onFeedbackSubmit }) => (
  <div
    className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
    data-testid={isUser ? 'user-message' : 'assistant-message'}
  >
    <div
      className={`max-w-3xl px-4 py-3 rounded-2xl ${
        isUser
          ? 'bg-blue-600 text-white'
          : 'bg-gray-100 text-gray-800 border border-gray-200'
      }`}
    >
      {!isUser && message.brand && (
        <div className="flex items-center gap-2 mb-2">
          <div
            className={`flex items-center gap-2 px-2 py-1 rounded-lg ${
              message.brand === 'visa'
                ? 'bg-blue-50 border border-blue-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <img 
              src={message.brand === 'visa' ? '/visa-logo.png' : '/mastercard-logo.png'}
              alt={message.brand === 'visa' ? 'Visa' : 'Mastercard'}
              className="h-4 w-auto"
            />
          </div>
        </div>
      )}
      {!isUser ? (
        <div className="markdown-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {message.content}
          </ReactMarkdown>
        </div>
      ) : (
        <div className="whitespace-pre-wrap">{message.content}</div>
      )}

      {!isUser && message.brandLeakage && message.brandLeakage.has_leakage && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg" data-testid="brand-leakage-warning">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-yellow-800">
                {message.brandLeakage.message}
              </p>
              {message.brandLeakage.wrong_references && (
                <ul className="text-xs text-yellow-700 mt-1 ml-4 list-disc">
                  {message.brandLeakage.wrong_references.map((ref, idx) => (
                    <li key={idx}>
                      {ref.file_name} (card: {ref.card})
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {!isUser && message.citations && message.citations.length > 0 && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" />
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Páginas: </span>
              {getUniquePages(message.citations).join(', ')}
            </p>
          </div>
        </div>
      )}

      {!isUser && message.usage && (
        <div className="mt-2 text-xs text-gray-500">
          Tokens: {message.usage.total_tokens} (prompt: {message.usage.prompt_tokens}, respuesta: {message.usage.completion_tokens})
        </div>
      )}

      {!isUser && message.traceId && onFeedbackSubmit && (
        <FeedbackSection 
          traceId={message.traceId} 
          onFeedbackSubmit={onFeedbackSubmit}
        />
      )}
    </div>
  </div>
);

function App() {
  const [messages, setMessages] = useState(() => {
    // Load messages from localStorage on initial render
    try {
      const savedMessages = localStorage.getItem('chatMessages');
      return savedMessages ? JSON.parse(savedMessages) : [];
    } catch (error) {
      console.error('Error loading messages from localStorage:', error);
      return [];
    }
  });
  const [inputValue, setInputValue] = useState('');
  const [selectedBrand, setSelectedBrand] = useState(() => {
    // Load selected brand from localStorage
    return localStorage.getItem('selectedBrand') || 'visa';
  });
  const [isLoading, setIsLoading] = useState(false);
  const [assistantStatus, setAssistantStatus] = useState(null);
  const [sessionId] = useState(() => {
    // Get existing session ID from localStorage or create a new one
    let existingSessionId = localStorage.getItem('sessionId');
    if (!existingSessionId) {
      existingSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('sessionId', existingSessionId);
    }
    return existingSessionId;
  });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('chatMessages', JSON.stringify(messages));
    } catch (error) {
      console.error('Error saving messages to localStorage:', error);
    }
  }, [messages]);

  // Save selected brand to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedBrand', selectedBrand);
  }, [selectedBrand]);

  useEffect(() => {
    // Check assistant status on mount
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/assistant/status`);
        setAssistantStatus(response.data);
      } catch (error) {
        console.error('Error al verificar estado del asistente:', error);
        setAssistantStatus({ status: 'error', message: 'Error de conexión' });
      }
    };
    checkStatus();
  }, []);

  const buildHistory = () => {
    const history = [];
    for (let i = 0; i < messages.length; i++) {
      if (messages[i].isUser) {
        const userMsg = messages[i].content;
        const assistantMsg = messages[i + 1] && !messages[i + 1].isUser ? messages[i + 1].content : '';
        if (assistantMsg) {
          history.push({
            user: userMsg,
            assistant: assistantMsg
          });
        }
      }
    }
    return history;
  };

  const handleFeedbackSubmit = async (traceId, rating, comment) => {
    try {
      await axios.post(`${API}/feedback`, {
        trace_id: traceId,
        user_score: rating,
        user_feedback: comment
      });
    } catch (error) {
      console.error('Error al enviar feedback:', error);
      throw error;
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      content: inputValue,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const history = buildHistory();
      const response = await axios.post(`${API}/chat`, {
        brand: selectedBrand,
        query: inputValue,
        history: history.length > 0 ? history : null,
        model: 'gpt-4o',
        temperature: 0.0,
        session_id: sessionId
      });

      const assistantMessage = {
        content: response.data.message.content,
        isUser: false,
        brand: selectedBrand,
        citations: response.data.citations,
        usage: response.data.usage,
        brandLeakage: response.data.brand_leakage,
        traceId: response.data.trace_id,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      const errorMessage = {
        content: `Error: ${error.response?.data?.detail || error.message || 'No se pudo obtener respuesta'}`,
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    if (window.confirm('¿Estás seguro de que deseas limpiar todo el historial del chat?')) {
      setMessages([]);
      localStorage.removeItem('chatMessages');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-6xl mx-auto h-screen flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 shadow-sm" data-testid="chat-header">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img 
                  src="/sistarbanc-logo.png" 
                  alt="Sistarbanc" 
                  className="h-12 w-auto"
                />
                <h1 className="text-2xl font-bold text-gray-800">Asistente de Manuales</h1>
              </div>
              <div className="flex items-center gap-4">
                <BrandSelector selectedBrand={selectedBrand} onBrandChange={setSelectedBrand} />
                {messages.length > 0 && (
                  <button
                    onClick={handleClearChat}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                    data-testid="clear-chat-button"
                    title="Limpiar historial"
                  >
                    <Trash2 className="w-4 h-4" />
                    Limpiar
                  </button>
                )}
              </div>
            </div>
            {assistantStatus && (
              <div className="mt-2">
                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    assistantStatus.status === 'connected'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                  data-testid="assistant-status"
                >
                  {assistantStatus.status === 'connected' ? '● Conectado' : '● Desconectado'}
                </span>
              </div>
            )}
          </div>
        </header>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-6 py-6" data-testid="messages-container">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mb-4">
                <Sparkles className="w-10 h-10 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Bienvenido al Asistente de los manuales de Visa y Mastercard</h2>
              <p className="text-gray-600 mb-4 max-w-md">
                Haz preguntas sobre especificaciones técnicas de Visa o Mastercard. ¡Selecciona tu sello y comienza a chatear!
              </p>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <Message 
                key={idx} 
                message={msg} 
                isUser={msg.isUser}
                onFeedbackSubmit={handleFeedbackSubmit}
              />
            ))
          )}
          {isLoading && (
            <div className="flex justify-start mb-4" data-testid="loading-indicator">
              <div className="bg-gray-100 px-4 py-3 rounded-2xl border border-gray-200">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="bg-white border-t border-gray-200 px-6 py-4 shadow-lg" data-testid="input-area">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <textarea
                data-testid="message-input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={`Pregunta sobre especificaciones de ${selectedBrand === 'visa' ? 'Visa' : 'Mastercard'}...`}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows="1"
                disabled={isLoading}
              />
            </div>
            <button
              data-testid="send-button"
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-medium"
            >
              <Send className="w-5 h-5" />
              Enviar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;